import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Formik } from "formik";
import ContentContainer from "templates/content-container/contentContainer";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import { PAGE_IDS } from "app/data/common/pageIds";
import Table from "ui-components/table/table";
import Button, { ButtonThemes, ButtonSizes } from "ui-components/button/button";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import { Popover } from "ui-components/popover/popover";
import { ReactComponent as TerminalActivityIcon } from "../../images/TerminalAtivityLog.svg";
import { usaStates } from "app/data/common/usaStates";
import {
  DIRECT_FILTER_OPTIONS,
  ROUTE_GUIDE_FIELD_HELP,
  ROUTE_GUIDE_FIELD_LABELS,
  ZipRoutingProfile,
  formatExtensionCode,
  formatServiceDays,
  formatStateDisplay,
  formatTerminalRichDisplay,
  formatThreePlLaneCode,
  getWeekdayServiceDayDisplayItems,
  resolveTerminalDisplayInput,
  EMPTY_SELECT_VALUE,
} from "app/data/zip-routing-profile/models";
import { useZipRoutingProfileList } from "hooks/useZipRoutingProfiles";
import { useTerminalsList } from "hooks/useEquipment";
import RouteGuideAddEdit from "./routeGuideAddEdit";
import RouteGuideActivityLog from "./activity-log/routeGuideActivityLog";
import "./routeGuide.scss";

interface Option {
  label: string;
  value: string;
}

const columnHelper = createColumnHelper<ZipRoutingProfile>();

const RouteGuide: React.FC = () => {
  const { data: terminalOptions = [], isLoading: terminalsLoading } = useTerminalsList();

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [stateOption, setStateOption] = useState<Option | null>(null);
  const [terminalOption, setTerminalOption] = useState<Option | null>(null);
  const [directOption, setDirectOption] = useState<Option | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const tableDisabled = showCreate || !!editingProfileId || showActivityLog;

  const terminalFilterOptions: Option[] = terminalOptions.map((t) => ({
    label: formatTerminalRichDisplay(t),
    value: String(t.terminalNumber),
  }));

  const terminalsByNumber = useMemo(() => {
    const map = new Map<string, { airportCode?: string; terminalName?: string }>();
    terminalOptions.forEach((t) => {
      if (t.terminalNumber == null) return;
      map.set(String(t.terminalNumber), {
        airportCode: t.airportCode,
        terminalName: t.terminalName,
      });
    });
    return map;
  }, [terminalOptions]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useZipRoutingProfileList({
    search: appliedSearch || undefined,
    state: stateOption?.value,
    terminalNumber: terminalOption?.value,
    direct: directOption?.value,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleScroll = useCallback(() => {
    const scrollContainer = document.querySelector(".xgs-route-guide__orders");
    if (!scrollContainer) return;

    const bottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop <=
      scrollContainer.clientHeight + 10;

    if (bottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const onSearchStart = useCallback(() => {
    setAppliedSearch(searchInput.trim());
  }, [searchInput]);

  const onSearchClear = () => {
    setSearchInput("");
    setAppliedSearch("");
    setStateOption(null);
    setTerminalOption(null);
    setDirectOption(null);
  };

  const columns = [
    columnHelper.accessor((row) => row.zipCode || row.zip || "", {
      id: "zipCode",
      header: () => (
        <>
          ZIP
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  ZIP code covered by this routing profile.
                </div>
              }
            />
          </span>
        </>
      ),
      size: 50,
    }),
    columnHelper.accessor("state", {
      id: "state",
      header: "State",
      cell: (cellProps) => formatStateDisplay(cellProps.getValue()),
      size: 110,
    }),
    columnHelper.accessor("terminalNumber", {
      id: "terminal",
      header: () => (
        <>
          Terminal
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Servicing terminal assigned from the route guide for this ZIP.
                </div>
              }
            />
          </span>
        </>
      ),
      cell: (cellProps) => {
        const row = cellProps.row.original;
        if (row.coverageStatus === "NOT_SERVICED" || row.terminalNumber == null) {
          return "—";
        }
        return formatTerminalRichDisplay(
          resolveTerminalDisplayInput(row, terminalsByNumber)
        );
      },
      size: 160,
    }),
    columnHelper.accessor((row) => row.coverageStatus || "SERVICED", {
      id: "coverageStatus",
      header: () => (
        <>
          Coverage
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Whether this ZIP is serviced or not serviced in the route guide.
                </div>
              }
            />
          </span>
        </>
      ),
      cell: (cellProps) => {
        const value = cellProps.getValue();
        const serviced = value !== "NOT_SERVICED";
        return (
          <Tag
            mods={{
              color: serviced ? TagColor.GREEN : TagColor.GREY,
              uppercase: true,
            }}
          >
            {serviced ? "Serviced" : "Not Serviced"}
          </Tag>
        );
      },
      size: 70,
    }),
    columnHelper.accessor(
      (row) => {
        if (typeof row.direct === "boolean") return row.direct;
        if (row.rateType === "I") return false;
        if (row.rateType === "D") return true;
        return true;
      },
      {
        id: "direct",
        header: () => (
          <>
            Delivery Point
            <span onClick={(e) => e.stopPropagation()}>
              <Popover
                values={
                  <div className="popover-value">
                    Direct vs indirect delivery point for this ZIP.
                  </div>
                }
              />
            </span>
          </>
        ),
        cell: (cellProps) => (
          <Tag
            mods={{
              color: cellProps.getValue() ? TagColor.GREEN : TagColor.ORANGE,
              uppercase: true,
            }}
          >
            {cellProps.getValue() ? "Direct" : "Indirect"}
          </Tag>
        ),
        size: 55,
      }
    ),
    // Quote column hidden for now — keep quoteAllowed in API payload defaults
    // columnHelper.accessor((row) => !!row.quoteAllowed, {
    //   id: "quoteAllowed",
    //   header: () => (
    //     <>
    //       Quote
    //       <span onClick={(e) => e.stopPropagation()}>
    //         <Popover
    //           values={
    //             <div className="popover-value">
    //               Whether rate quote is allowed for this ZIP.
    //             </div>
    //           }
    //         />
    //       </span>
    //     </>
    //   ),
    //   cell: (cellProps) => (
    //     <Tag
    //       mods={{
    //         color: cellProps.getValue() ? TagColor.GREEN : TagColor.GREY,
    //         uppercase: true,
    //       }}
    //     >
    //       {formatQuoteAllowed(cellProps.getValue())}
    //     </Tag>
    //   ),
    //   size: 45,
    // }),
    columnHelper.accessor((row) => formatThreePlLaneCode(row.threePlOriginCode), {
      id: "obm",
      header: () => (
        <>
          {ROUTE_GUIDE_FIELD_LABELS.threePlOriginCode}
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  {ROUTE_GUIDE_FIELD_HELP.threePlOriginCode}
                </div>
              }
            />
          </span>
        </>
      ),
      size: 70,
    }),
    columnHelper.accessor((row) => formatThreePlLaneCode(row.threePlDestCode), {
      id: "ibm",
      header: () => (
        <>
          {ROUTE_GUIDE_FIELD_LABELS.threePlDestCode}
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  {ROUTE_GUIDE_FIELD_HELP.threePlDestCode}
                </div>
              }
            />
          </span>
        </>
      ),
      size: 80,
    }),
    columnHelper.accessor((row) => formatExtensionCode(row.extensionCode1), {
      id: "extra1",
      header: () => (
        <>
          {ROUTE_GUIDE_FIELD_LABELS.extensionCode1}
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  {ROUTE_GUIDE_FIELD_HELP.extensionCode1}
                </div>
              }
            />
          </span>
        </>
      ),
      size: 70,
    }),
    columnHelper.accessor((row) => formatExtensionCode(row.extensionCode2), {
      id: "extra2",
      header: () => (
        <>
          {ROUTE_GUIDE_FIELD_LABELS.extensionCode2}
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  {ROUTE_GUIDE_FIELD_HELP.extensionCode2}
                </div>
              }
            />
          </span>
        </>
      ),
      size: 70,
    }),
    columnHelper.display({
      id: "serviceDays",
      header: () => (
        <>
          Service Days
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Mon–Fri always shown. Same Day, Interline, 0–9 days, or No Delivery
                  when blank.
                </div>
              }
            />
          </span>
        </>
      ),
      cell: (cellProps) => {
        const items = getWeekdayServiceDayDisplayItems(cellProps.row.original);
        return (
          <div
            className="xgs-route-guide__days-cell"
            title={formatServiceDays(cellProps.row.original)}
          >
            {items.map(({ day, label, code, display }) => {
              const isEmpty = !code || code === EMPTY_SELECT_VALUE;
              return (
                <div
                  key={day}
                  className={
                    isEmpty
                      ? "xgs-route-guide__days-cell__item xgs-route-guide__days-cell__item--empty"
                      : "xgs-route-guide__days-cell__item"
                  }
                >
                  <span className="xgs-route-guide__days-cell__label">{label}</span>
                  {isEmpty ? (
                    <span className="xgs-route-guide__days-cell__code">No Delivery</span>
                  ) : (
                    <span className="xgs-route-guide__days-cell__code">{display}</span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
      size: 360,
    }),
    columnHelper.accessor("dateUpdated", {
      id: "dateUpdated",
      header: "Last Updated",
      cell: (cellProps) => {
        const value = cellProps.getValue();
        if (!value?.trim()) return "—";
        return value.toUiDateTimeFormat?.() || value;
      },
      size: 90,
    }),
  ];

  return (
    <ContentContainer
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.ROUTE_GUIDE}
          title="Route Guide"
          status="PREVIEW"
          tooltip="Routing Guide Maintenance (replaces AS400 RTE021). Large catalog — filters use server-side pagination."
          className="xgs-route-guide-toolbar"
        >
          <div className="xgs-route-guide-toolbar__activity-log">
            <Button
              theme={ButtonThemes.transparent}
              size={ButtonSizes.auto}
              onClick={() => setShowActivityLog(true)}
              disabled={tableDisabled && !showActivityLog}
            >
              <div className="xgs-btn__icon-container">
                <TerminalActivityIcon className="xgs-btn__icon" />
              </div>
              Route Guide Activity Log
            </Button>
          </div>
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-route-guide">
        <div className="xgs-list__controls">
          <Formik
            initialValues={{
              state: stateOption?.value || "",
              terminal: terminalOption?.value || "",
              direct: directOption?.value || "",
            }}
            enableReinitialize
            onSubmit={onSearchStart}
          >
            {() => (
              <div className="xgs-list__controls__search">
                <LabeledTextInput
                  label="Search ZIP"
                  labelMode={LabelModes.column}
                  className="xgs-list__controls__search__input"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !isLoading && setAppliedSearch(searchInput.trim())
                  }
                  type="text"
                  disabled={tableDisabled}
                  placeholder="Search by ZIP code..."
                />

                <LabeledSelectInput
                  label="State"
                  labelMode={LabelModes.column}
                  isClearable
                  isSearchable
                  onValueChange={(opt) => setStateOption(opt as Option | null)}
                  value={stateOption}
                  options={usaStates as Option[]}
                  className="xgs-item-form__field filter-select"
                  disabled={tableDisabled}
                  placeholder="All States"
                />

                <LabeledSelectInput
                  label="Terminal"
                  labelMode={LabelModes.column}
                  isClearable
                  isSearchable
                  isLoading={terminalsLoading}
                  onValueChange={(opt) => setTerminalOption(opt as Option | null)}
                  value={terminalOption}
                  options={terminalFilterOptions}
                  className="xgs-item-form__field filter-select"
                  disabled={tableDisabled}
                  placeholder="All Terminals"
                />

                <LabeledSelectInput
                  label="Delivery Point"
                  labelMode={LabelModes.column}
                  isClearable
                  isSearchable={false}
                  onValueChange={(opt) => setDirectOption(opt as Option | null)}
                  value={directOption}
                  options={DIRECT_FILTER_OPTIONS}
                  className="xgs-item-form__field filter-select"
                  disabled={tableDisabled}
                  placeholder="All Delivery Points"
                />

                <div className="xgs-route-guide__filter-actions">
                  <Button
                    className="xgs-list__controls__button"
                    theme={ButtonThemes.blue}
                    disabled={isLoading || tableDisabled}
                    onClick={onSearchStart}
                  >
                    Search
                  </Button>

                  <Button
                    className="xgs-list__controls__button"
                    theme={ButtonThemes.gray}
                    disabled={tableDisabled}
                    onClick={onSearchClear}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            )}
          </Formik>

          <div className="xgs-list__controls__actions">
            <Button
              className="xgs-route-guide__create-button"
              theme={ButtonThemes.blue}
              disabled={tableDisabled}
              onClick={() => setShowCreate(true)}
            >
              + Add Profile
            </Button>
          </div>
        </div>

        {error && <XGSErrorMessage>{(error as Error)?.message || String(error)}</XGSErrorMessage>}

        {!error && (
          <div className="xgs-route-guide__orders" style={{ position: "relative" }}>
            <Table
              isLoading={isLoading}
              columns={columns}
              data={data?.allItems || []}
              noResultsText="There are no route guide profiles available"
              infiniteScroll
              infiniteScrollLoading={isFetchingNextPage}
              infiniteScrollHasNext={!!hasNextPage}
              onInfiniteScroll={handleScroll}
              responsive={true}
              sorting={true}
              minTableHeight={420}
              cursorPointer={!tableDisabled}
              onRowClicked={(row: ZipRoutingProfile) => {
                if (!tableDisabled && row?.id) {
                  setEditingProfileId(row.id);
                }
              }}
              autoResetSelectedRows={true}
            />
          </div>
        )}

        <RouteGuideAddEdit
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setAppliedSearch("");
            refetch();
          }}
        />

        <RouteGuideAddEdit
          show={!!editingProfileId}
          profileId={editingProfileId}
          onClose={() => setEditingProfileId(null)}
          onSaved={() => refetch()}
        />

        <RouteGuideActivityLog
          show={showActivityLog}
          onClose={() => setShowActivityLog(false)}
        />
      </div>
    </ContentContainer>
  );
};

export default RouteGuide;
